import { ReferenceItem } from '@/app/services/llm';
import { BatchDocDetail, getDocDetails } from '@/app/services/session';
import { getReferenceDocId } from '@/lib/reference-doc-id';
import { useEffect, useMemo, useState } from 'react';

type HydratedReferenceListener = (reference: BatchDocDetail) => void;

const hydratedReferenceListeners = new Set<HydratedReferenceListener>();

export function publishHydratedReference(reference: BatchDocDetail) {
  if (!reference.doc_id) return;
  hydratedReferenceListeners.forEach((listener) => listener(reference));
}

function mergeReference(
  reference: ReferenceItem,
  doc: BatchDocDetail | null | undefined,
): ReferenceItem {
  const fallbackDocId = getReferenceDocId(reference);
  if (!doc) return { ...reference, doc_id: reference.doc_id || fallbackDocId };

  return {
    ...reference,
    ...doc,
    doc_id: doc.doc_id ?? reference.doc_id ?? fallbackDocId,
    search_gateway: doc.search_gateway ?? reference.search_gateway,
    title: doc.title ?? reference.title,
    abstract: doc.abstract ?? reference.abstract,
    publisher: doc.publication ?? doc.publisher ?? reference.publisher,
    publisher_abbr:
      doc.publication_abbr ?? doc.publisher_abbr ?? reference.publisher_abbr,
    pub_day:
      doc.pub_day == null ? reference.pub_day : String(doc.pub_day),
    translation: doc.translation ?? reference.translation,
  };
}

function mergeDocDetail(
  current: BatchDocDetail | null | undefined,
  next: BatchDocDetail,
): BatchDocDetail {
  return {
    ...(current ?? {}),
    ...next,
    doc_id: next.doc_id ?? current?.doc_id,
    translation: next.translation ?? current?.translation,
  };
}

export function useHydratedReferences(references: ReferenceItem[]) {
  const [docMap, setDocMap] = useState<
    Record<string, BatchDocDetail | null | undefined>
  >({});
  const docIds = useMemo(
    () => Array.from(new Set(references.map(getReferenceDocId).filter(Boolean))),
    [references],
  );
  const docIdsKey = docIds.join('\n');

  useEffect(() => {
    const listener: HydratedReferenceListener = (reference) => {
      const docId = reference.doc_id?.trim();
      if (!docId) return;
      setDocMap((prev) => ({
        ...prev,
        [docId]: mergeDocDetail(prev[docId], reference),
      }));
    };

    hydratedReferenceListeners.add(listener);
    return () => {
      hydratedReferenceListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    const missingDocIds = docIds.filter((docId) => docMap[docId] === undefined);

    if (missingDocIds.length === 0) return;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      getDocDetails(missingDocIds)
        .then(({ data }) => {
          if (cancelled) return;
          setDocMap((prev) => {
            const next = { ...prev };
            missingDocIds.forEach((docId, index) => {
              next[docId] = data.docs?.[index] ?? null;
            });
            return next;
          });
        })
        .catch(() => {
          if (cancelled) return;
          setDocMap((prev) => {
            const next = { ...prev };
            missingDocIds.forEach((docId) => {
              next[docId] = null;
            });
            return next;
          });
        });
    }, 150);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [docIds, docIdsKey, docMap]);

  return useMemo(
    () =>
      references.map((reference) =>
        mergeReference(reference, docMap[getReferenceDocId(reference)]),
      ),
    [docMap, references],
  );
}
